import {
  AccordionDetails,
  alpha,
  AvatarGroup,
  Box,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@mui/icons-material';
import { StatusIcon } from './StatusIcon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DrawerAccordion,
  DrawerAccordionSummary,
  findStepByIdInSteps,
  IFlow,
  IStep,
  IWork,
  LayoutComponentNames,
  LEFT_DRAWER_MOBILE_BREAKPOINT,
  scrollToElement,
  StatusEnum,
  useEllipsis,
  usePrevious,
  UserAvatar,
  useWorkPageStore,
  WorkLayoutRenderingMode,
  getLayoutComponentLabel,
  isLayoutComponentConditionMet,
  shouldShowSectionComponentHeader,
  useWorkTaskActions,
  isUserAccessor,
  Popover,
  useWorkTaskToolbarTagPopover,
  WorkTaskToolbarStatusTooltipItem,
  useLayoutWithAppliedRepeaters,
} from '@diana/core';
import { Section } from './Section';
import { StepTaskProps, TaskProps, TaskStatusProps } from './Workflow.types';
import { useColor } from '../../Hooks';

export function TaskStatus({ step, work }: TaskStatusProps) {
  const { invitedEditorUsers } = useWorkTaskActions({ step, work });
  const { tooltipAnchor, onMouseEnterTag, onMouseLeaveTag } = useWorkTaskToolbarTagPopover();

  const isShowingUserAvatar = [StatusEnum.IN_PROGRESS, StatusEnum.PENDING].includes(step.status);
  if (!isShowingUserAvatar) return null;
  return (
    <>
      <Stack direction="row" alignItems="center" onClick={(e) => e.stopPropagation()}>
        <AvatarGroup
          onMouseEnter={onMouseEnterTag}
          onMouseLeave={onMouseLeaveTag}
          max={3}
          sx={{
            alignItems: 'center',
            '& .MuiAvatarGroup-avatar': {
              width: 22,
              height: 22,
              m: 0,
              ml: '-7px',
              mr: 0.5,
              zIndex: 0,
              fontSize: '13px',
              boxShadow: 'none',
            },
          }}
        >
          {invitedEditorUsers.filter(isUserAccessor).map((accessor) => {
            return (
              <UserAvatar
                key={accessor.user?.id}
                tooltip={false}
                user={accessor.user}
                sx={{
                  textTransform: 'uppercase',
                  width: 22,
                  height: 22,
                  fontSize: 'inherit',
                  m: 0,
                }}
              />
            );
          })}
        </AvatarGroup>
      </Stack>
      <Popover
        disableAutoFocus
        open={Boolean(tooltipAnchor)}
        anchorEl={tooltipAnchor}
        sx={{ pointerEvents: 'none', mt: 1 }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box
          onMouseEnter={() => onMouseLeaveTag.cancel()}
          onMouseLeave={onMouseLeaveTag}
          sx={{ pointerEvents: 'all', p: 1.5 }}
        >
          {invitedEditorUsers.filter(isUserAccessor).map((accessor) => {
            return <WorkTaskToolbarStatusTooltipItem key={accessor.user.id} user={accessor.user} />;
          })}
        </Box>
      </Popover>
    </>
  );
}

export function Task({ step, closeDrawer, work }: TaskProps) {
  const { breakpoints, palette, shape } = useTheme();
  const renderingMode = useWorkPageStore((s) => s.renderingMode);
  const setActiveTaskId = useWorkPageStore((s) => s.setActiveTaskId);
  const activeTaskId = useWorkPageStore((s) => s.activeTaskId);
  const workId = useWorkPageStore((s) => s.work?.id);
  const isMobileDrawer = useMediaQuery(breakpoints.down(LEFT_DRAWER_MOBILE_BREAKPOINT));
  const color = useColor({ step });
  const ref = useRef<HTMLDivElement>();

  const isPageRenderingMode = renderingMode === WorkLayoutRenderingMode.Page;
  const isTaskShownInPage = isPageRenderingMode && activeTaskId === step.id;

  const onTaskClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      if (renderingMode === WorkLayoutRenderingMode.Page) {
        setActiveTaskId(step.id);
      } else {
        scrollToElement({ e, id: step.id, isBoundingClientRect: true });
      }

      isMobileDrawer && closeDrawer();
    },
    [closeDrawer, isMobileDrawer, renderingMode, setActiveTaskId, step.id],
  );

  const title = useEllipsis({
    text: step.displayName,
    sx: { cursor: 'pointer', color, lineHeight: `2rem` },
  });

  const background = step.isActive
    ? alpha(palette.primary.main, 0.2)
    : isTaskShownInPage
    ? palette.background.card
    : 'transparent';

  const layout = useLayoutWithAppliedRepeaters({
    layout: step.layout,
    data: work.data,
    flowId: work.flowId,
  });

  const sections = useMemo(() => {
    return layout?.components.filter((section, index) => {
      if (!section?.name || section?.component !== LayoutComponentNames.SECTION) return false;

      if (!isLayoutComponentConditionMet(section.condition, { ...work?.data, '@work': work })) {
        return false;
      }

      return shouldShowSectionComponentHeader({
        sectionIndex: index,
        sectionLabel: getLayoutComponentLabel(section),
        taskName: step.name,
        taskDisplayName: step.displayName,
      });
    });
  }, [layout?.components, step.name, step.displayName, work]);

  return (
    <Box sx={{ '&:not(:first-child)': { mt: 2.5 } }} ref={ref}>
      <Box sx={{ width: '100%', mt: -0.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{
            width: '100%',
            background,
            pl: 1,
            pr: 0.5,
            py: 0.5,
            borderRadius: +shape.borderRadius / 4,
            overflowX: 'hidden',
          }}
        >
          <Stack gap={1.5} direction="row" sx={{ overflowX: 'hidden' }}>
            <StatusIcon work={work} step={step} size="small" />
            <Box onClick={onTaskClick} sx={{ overflowX: 'hidden' }}>
              {title}
            </Box>
          </Stack>
          <TaskStatus step={step} work={work} />
        </Stack>
      </Box>
      <Box sx={{ mt: -0.5, ml: 1 }}>
        {sections?.map((section, i) => (
          <Section
            key={section.id + sections.length}
            workId={workId!}
            component={section}
            step={step}
            color={color}
            closeDrawer={closeDrawer}
            work={work}
            index={i}
          />
        ))}
      </Box>
    </Box>
  );
}

function getDefaultTask(steps: IStep[], stepId: string) {
  return findStepByIdInSteps(steps, stepId)!.steps[0].id;
}

export function Step({ step, steps, work, closeDrawer }: StepTaskProps) {
  const { breakpoints } = useTheme();
  const renderingMode = useWorkPageStore((s) => s.renderingMode);
  const activeTaskId = useWorkPageStore((s) => s.activeTaskId);
  const setActiveTaskId = useWorkPageStore((s) => s.setActiveTaskId);
  const isMobileDrawer = useMediaQuery(breakpoints.down(LEFT_DRAWER_MOBILE_BREAKPOINT));
  const color = useColor({ step });

  const hasActiveTask = !!step.steps.find((s) => s.id === activeTaskId);
  const [expanded, setExpanded] = useState<boolean>(hasActiveTask);
  const prevExpanded = usePrevious(expanded);

  const isExpanded = useMemo(() => {
    return step.userPermissions?.some((v) => v === 'PreviewMode') || hasActiveTask
      ? !(prevExpanded && !expanded)
      : expanded;
  }, [step.userPermissions, hasActiveTask, prevExpanded, expanded]);

  useEffect(() => {
    if (expanded && !hasActiveTask) setExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveTask]);

  const title = useEllipsis({
    text: step.displayName,
    sx: { cursor: 'pointer', lineHeight: `${24 / 14}rem`, color },
  });

  const onStepClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();

      if (renderingMode === WorkLayoutRenderingMode.Page) {
        setActiveTaskId(getDefaultTask(steps, step.id));

        if (hasActiveTask && expanded) {
          setExpanded(false);
        } else if (!expanded) {
          setExpanded(true);
        }
      } else {
        scrollToElement({ e, id: step.id, isBoundingClientRect: true });
      }

      isMobileDrawer && closeDrawer();
    },
    [
      closeDrawer,
      expanded,
      hasActiveTask,
      isMobileDrawer,
      renderingMode,
      setActiveTaskId,
      step.id,
      steps,
    ],
  );

  return (
    <DrawerAccordion
      sx={{
        px: 2,
        py: 1,
        '& .MuiButtonBase-root': {
          minHeight: '36px !important',
        },
      }}
      expanded={isExpanded}
      TransitionProps={{ unmountOnExit: true }}
    >
      <DrawerAccordionSummary
        expandIcon={<ExpandMoreRoundedIcon style={{ fontSize: '24px' }} />}
        onClick={() => setExpanded(!expanded)}
        sx={{ '& .MuiAccordionSummary-content': { overflowX: 'hidden' } }}
      >
        <Box onClick={onStepClick} sx={{ overflowX: 'hidden' }}>
          {title}
        </Box>
      </DrawerAccordionSummary>
      <AccordionDetails sx={{ p: 0, mt: 0.5 }}>
        {step.steps.map((step) => (
          <Task key={step.id} work={work} step={step} closeDrawer={closeDrawer} />
        ))}
      </AccordionDetails>
    </DrawerAccordion>
  );
}

export function Steps({
  steps,
  flow,
  work,
  closeDrawer,
}: {
  steps?: IStep[];
  flow: IFlow;
  work: IWork;
  closeDrawer: () => void;
}) {
  return (
    <Box>
      {steps?.map((step) => (
        <Step
          key={step.id}
          work={work}
          step={step}
          steps={steps}
          flow={flow}
          closeDrawer={closeDrawer}
        />
      ))}
    </Box>
  );
}
